import React from "react";

import { useSelector } from "react-redux";

import {
  ReviewCard,
  ReviewCardTitle,
  ReviewField,
  ReviewFieldGrid,
} from "components/ReviewCard";
import { Text } from "components/Text";

import { formatDollarsFromCents } from "lib";
import { formatAddress } from "lib/formatAddress";
import { formatDate } from "lib/timeFormats";

import { getManualAdjustments } from "selectors";

import { getPrimaryContactFromBusiness } from "lib/businesses";

import {
  computeLotDisplayValues,
  useEContractReviewData,
} from "hooks/useEContractReviewData";
import { eContractFieldInfoText } from "constants/eContract";

interface EContractRMAReviewSectionProps {
  agreementId: string;
}

function transitInsuranceText(business: Business, isBuyer?: boolean): string {
  if (business.hasTransitInsuranceForSales && !isBuyer) return "Yes";
  if (business.hasTransitInsuranceForPurchases && isBuyer) return "Yes";
  return "No";
}

function formatContactText(business: Business): string {
  const contact = getPrimaryContactFromBusiness(business);
  if (!contact) return "-";
  return (
    [contact.firstName, contact.lastName, contact.phoneNumber]
      .filter(Boolean)
      .join(" ") || "-"
  );
}

export const EContractRMAReviewSection = ({
  agreementId,
}: EContractRMAReviewSectionProps): React.JSX.Element => {
  const {
    vendor,
    buyer,
    lots,
    sale,
    sexes,
    ageOptions,
    breedOptions,
    originatingPICs,
    destinationPICs,
    nvds,
    singleDeliveryDate,
  } = useEContractReviewData(agreementId);

  const allAdjustments = useSelector(getManualAdjustments);
  const charges = Object.values(allAdjustments).filter(
    (adj: ManualAdjustment) => {
      return (
        adj.livestockSaleId === sale?.livestocksale_id &&
        ((vendor &&
          (adj.fromBusinessId === vendor.id ||
            adj.toBusinessId === vendor.id)) ||
          (buyer &&
            (adj.fromBusinessId === buyer.id || adj.toBusinessId === buyer.id)))
      );
    },
  );

  return (
    <>
      {/* Vendor */}
      <ReviewCard>
        <ReviewCardTitle>Vendor</ReviewCardTitle>
        <ReviewFieldGrid>
          <ReviewField
            xs={7}
            md={10}
            label="Vendor Name"
            infoText={eContractFieldInfoText.vendorName}
          >
            {vendor?.name ?? "-"}
          </ReviewField>
          <ReviewField
            xs={5}
            md={2}
            label="PIC No"
            infoText={eContractFieldInfoText.vendorPicNo}
          >
            {originatingPICs.length > 0 ? originatingPICs.join(", ") : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Address"
            infoText={eContractFieldInfoText.vendorAddress}
          >
            {vendor ? formatAddress(vendor.address) ?? "-" : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Contact"
            infoText={eContractFieldInfoText.vendorContact}
          >
            {vendor ? formatContactText(vendor) : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={6}
            label="NVD No"
            infoText={eContractFieldInfoText.nvdNo}
          >
            {nvds.length > 0 ? nvds.join(", ") : "-"}
          </ReviewField>
          <ReviewField
            xs={6}
            label="Transit Insurance"
            infoText={eContractFieldInfoText.vendorTransitInsurance}
          >
            {vendor ? transitInsuranceText(vendor, false) : "-"}
          </ReviewField>
        </ReviewFieldGrid>
      </ReviewCard>

      {/* Purchaser */}
      <ReviewCard>
        <ReviewCardTitle>Purchaser</ReviewCardTitle>
        <ReviewFieldGrid>
          <ReviewField
            xs={7}
            md={10}
            label="Purchaser Name"
            infoText={eContractFieldInfoText.purchaserName}
          >
            {buyer?.name ?? "-"}
          </ReviewField>
          <ReviewField
            xs={5}
            md={2}
            label="PIC No"
            infoText={eContractFieldInfoText.purchaserPicNo}
          >
            {destinationPICs.length > 0 ? destinationPICs.join(", ") : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Address"
            infoText={eContractFieldInfoText.purchaserAddress}
          >
            {buyer ? formatAddress(buyer.address) ?? "-" : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Contact"
            infoText={eContractFieldInfoText.purchaserContact}
          >
            {buyer ? formatContactText(buyer) : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Transit Insurance"
            infoText={eContractFieldInfoText.purchaserTransitInsurance}
          >
            {buyer ? transitInsuranceText(buyer, true) : "-"}
          </ReviewField>
        </ReviewFieldGrid>
      </ReviewCard>

      {/* Sale Details */}
      <ReviewCard>
        <ReviewCardTitle>Sale</ReviewCardTitle>
        <ReviewFieldGrid>
          <ReviewField
            xs={6}
            label="Sale Date"
            infoText={eContractFieldInfoText.saleDate}
          >
            {sale?.date ? formatDate(sale.date) : "-"}
          </ReviewField>
          <ReviewField
            xs={6}
            label="Delivery Date"
            infoText={eContractFieldInfoText.deliveryDate}
          >
            {singleDeliveryDate ? formatDate(singleDeliveryDate) : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Delivery Location"
            infoText={eContractFieldInfoText.deliveryLocation}
          >
            {buyer ? formatAddress(buyer.address) ?? "-" : "-"}
          </ReviewField>
        </ReviewFieldGrid>
      </ReviewCard>

      {/* Livestock */}
      {lots.length === 0 ? (
        <ReviewCard>
          <ReviewCardTitle>Livestock</ReviewCardTitle>
          <Text italic color="titleMedium">
            No lots found for this agreement.
          </Text>
        </ReviewCard>
      ) : (
        lots.map(lot => {
          const { description, unitPrice, weightKg } = computeLotDisplayValues(
            lot,
            sexes,
            ageOptions,
            breedOptions,
          );
          const marksStr =
            lot.marks
              ?.map(m => m.location)
              .filter(Boolean)
              .join(", ") || null;

          return (
            <ReviewCard key={lot.id}>
              <ReviewCardTitle>Lot {lot.lot_number}</ReviewCardTitle>

              <ReviewFieldGrid>
                <ReviewField
                  xs={6}
                  label="Qty"
                  infoText={eContractFieldInfoText.lotQty}
                >
                  {lot.quantity || "-"}
                </ReviewField>

                <ReviewField
                  xs={6}
                  label="Hd Delivered"
                  infoText={eContractFieldInfoText.lotHdDelivered}
                >
                  {lot.quantity_delivered ?? "-"}
                </ReviewField>
              </ReviewFieldGrid>

              <ReviewFieldGrid>
                <ReviewField
                  xs={6}
                  label="Description"
                  infoText={eContractFieldInfoText.lotDescription}
                >
                  {description}
                </ReviewField>
                <ReviewField
                  xs={6}
                  label="Price"
                  infoText={eContractFieldInfoText.lotPrice}
                >
                  {unitPrice ?? "-"}
                </ReviewField>
              </ReviewFieldGrid>

              <ReviewFieldGrid>
                <ReviewField
                  xs={6}
                  label="Weight"
                  infoText={eContractFieldInfoText.lotWeight}
                >
                  {weightKg ?? "-"}
                </ReviewField>
                <ReviewField
                  xs={6}
                  label="Marks (Identifiers)"
                  infoText={eContractFieldInfoText.lotMarks}
                >
                  {marksStr ?? "-"}
                </ReviewField>
              </ReviewFieldGrid>

              <ReviewFieldGrid>
                <ReviewField
                  xs={12}
                  label="Lot Notes"
                  infoText={eContractFieldInfoText.lotNotes}
                >
                  {lot.note || "-"}
                </ReviewField>
              </ReviewFieldGrid>
            </ReviewCard>
          );
        })
      )}

      {/* Charges */}
      <ReviewCard>
        <ReviewCardTitle>Charges</ReviewCardTitle>
        {charges.length === 0 ? (
          <Text italic color="titleMedium">
            No additional charges found for this agreement.
          </Text>
        ) : (
          charges.map((charge: ManualAdjustment) => (
            <React.Fragment key={charge.id}>
              <ReviewFieldGrid>
                <ReviewField
                  xs={8}
                  label="Description"
                  infoText={eContractFieldInfoText.chargeDescription}
                >
                  {charge.note || "-"}
                </ReviewField>
                <ReviewField
                  xs={4}
                  label="Amount"
                  infoText={eContractFieldInfoText.chargeAmount}
                >
                  {charge.subtotalCents != null
                    ? formatDollarsFromCents(charge.subtotalCents)
                    : "TBA"}
                </ReviewField>
              </ReviewFieldGrid>
              <ReviewFieldGrid>
                <ReviewField
                  xs={12}
                  label="Supplier / Invoice"
                  infoText={eContractFieldInfoText.chargeSupplierInvoice}
                >
                  {charge.billingReference ?? "-"}
                </ReviewField>
              </ReviewFieldGrid>
            </React.Fragment>
          ))
        )}
      </ReviewCard>
    </>
  );
};
